import { authProcessors } from '@features/auth/model/authProcessors.js';
import { authDeleteAccountProcessor } from '@features/auth/model/processors/authDeleteAccountProcessor.js';
import { authDisconnectProcessor } from '@features/auth/model/processors/authDisconnectProcessor.js';
import {
  authInitProcessor,
  authTrackProcessor,
} from '@features/auth/model/processors/authInitProcessor.js';
import { authLogoutProcessor } from '@features/auth/model/processors/authLogoutProcessor.js';
import {
  browseCacheClearProcessor,
  browseCacheLayersProcessor,
  browseCacheOpenProcessor,
  browseCacheSettingsProcessor,
} from '@features/cachedMaps/model/browseCacheProcessor.js';
import {
  cachedMapDeletedProcessor,
  cachedMapEditedProcessor,
  cacheTilesRestartProcessor,
  cacheTilesStartProcessor,
  cacheTilesStopProcessor,
} from '@features/cachedMaps/model/cacheTilesProcessor.js';
import {
  changesetsHistoryProcessor,
  changesetsHistorySelectionProcessor,
  changesetsReturnToHistoryProcessor,
} from '@features/changesets/model/historyProcessor.js';
import { changesetInspectorProcessor } from '@features/changesets/model/inspectorToast.js';
import { changesetsInspectProcessor } from '@features/changesets/model/inspectProcessor.js';
import {
  changesetsProcessor,
  changesetsTrackProcessor,
} from '@features/changesets/model/processor.js';
import { cookieConsentProcessor } from '@features/cookieConsent/model/processor.js';
import { dataViewerDeleteFeatureProcessor } from '@features/dataViewer/model/processors/dataViewerDeleteFeatureProcessor.js';
import { dataViewerDensifyProcessor } from '@features/dataViewer/model/processors/dataViewerDensifyProcessor.js';
import { dataViewerDownloadTrackProcessor } from '@features/dataViewer/model/processors/dataViewerDownloadTrackProcessor.js';
import { dataViewerGpxLoadProcessor } from '@features/dataViewer/model/processors/dataViewerGpxLoadProcessor.js';
import { dataViewerJoinTracksProcessor } from '@features/dataViewer/model/processors/dataViewerJoinTracksProcessor.js';
import { dataViewerMatchTrackProcessor } from '@features/dataViewer/model/processors/dataViewerMatchTrackProcessor.js';
import { dataViewerResolveElevationPromptProcessor } from '@features/dataViewer/model/processors/dataViewerResolveElevationPromptProcessor.js';
import { dataViewerSelectProcessor } from '@features/dataViewer/model/processors/dataViewerSelectProcessor.js';
import { dataViewerSetTrackDataProcessor } from '@features/dataViewer/model/processors/dataViewerSetTrackDataProcessor.js';
import {
  dataViewerForgetStoredProcessor,
  dataViewerRestoreStoredProcessor,
  dataViewerStoreProcessor,
} from '@features/dataViewer/model/processors/dataViewerStoreProcessors.js';
import { dataViewerToggleElevationChartProcessor } from '@features/dataViewer/model/processors/dataViewerToggleElevationChartProcessor.js';
import {
  dataViewerCheckOsmAuthorizationProcessor,
  dataViewerUploadToOsmProcessor,
} from '@features/dataViewer/model/processors/dataViewerUploadToOsmProcessor.js';
import { drawingDiscardPartialLineProcessor } from '@features/drawing/model/processors/drawingDiscardPartialLineProcessor.js';
import { elevationChartPendingTargetProcessor } from '@features/elevationChart/model/pendingTargetProcessor.js';
import { elevationChartProcessor } from '@features/elevationChart/model/processor.js';
import { galleryAllOfLicenseProcessor } from '@features/gallery/model/processors/galleryAllOfLicenseProcessor.js';
import { galleryDeletePictureProcessor } from '@features/gallery/model/processors/galleryDeletePictureProcessor.js';
import { galleryFetchUsersProcessor } from '@features/gallery/model/processors/galleryFetchUsersProcessor.js';
import { galleryItemUploadProcessor } from '@features/gallery/model/processors/galleryItemUploadProcessor.js';
import { galleryMakeAllPremiumOrFreeProcessor } from '@features/gallery/model/processors/galleryMakeAllPremiumOrFreeProcessor.js';
import { galleryQuickAddTagProcessor } from '@features/gallery/model/processors/galleryQuickAddTagProcessor.js';
import { galleryRequestImageProcessor } from '@features/gallery/model/processors/galleryRequestImageProcessor.js';
import { galleryRequestImagesByOrderProcessor } from '@features/gallery/model/processors/galleryRequestImagesByOrderProcessor.js';
import { galleryRequestImagesByRadiusProcessor } from '@features/gallery/model/processors/galleryRequestImagesByRadiusProcessor.js';
import { gallerySavePictureProcessor } from '@features/gallery/model/processors/gallerySavePictureProcessor.js';
import { gallerySetItemForPositionPickingProcessor } from '@features/gallery/model/processors/gallerySetItemForPositionPickingProcessor.js';
import { galleryShowImageGaProcessor } from '@features/gallery/model/processors/galleryShowImageGaProcessor.js';
import { galleryShowOnTheMapProcessor } from '@features/gallery/model/processors/galleryShowOnTheMapProcessor.js';
import { gallerySubmitCommentProcessor } from '@features/gallery/model/processors/gallerySubmitCommentProcessor.js';
import { gallerySubmitStarsProcessor } from '@features/gallery/model/processors/gallerySubmitStarsProcessor.js';
import { galleryUploadModalProcessor } from '@features/gallery/model/processors/galleryUploadModalProcessor.js';
import { geoipProcessor } from '@features/geoip/model/processors/geoIpProcessor.js';
import {
  gpsRecorderClearProcessor,
  gpsRecorderPauseProcessor,
  gpsRecorderPushedStatusProcessor,
  gpsRecorderStartProcessor,
  gpsRecorderStopProcessor,
  gpsRecorderSyncProcessor,
  gpsRecorderToolProcessor,
} from '@features/gpsRecorder/model/processors.js';
import { l10nSetLanguageProcessor } from '@features/l10n/model/processor.js';
import { legendProcessor } from '@features/legend/model/legendProcessor.js';
import { followLocationProcessor } from '@features/location/model/followProcessor.js';
import {
  fixReadyProcessor,
  locateOnceProcessor,
} from '@features/location/model/locateOnceProcessor.js';
import { locateProcessor } from '@features/location/model/locateProcessor.js';
import { getCountriesProcessor } from '@features/map/model/processors/getCountriesProcessor.js';
import { mapFitBboxProcessor } from '@features/map/model/processors/mapFitBboxProcessor.js';
import { mapRefocusProcessor } from '@features/map/model/processors/mapRefocusProcessor.js';
import { mapSettingsSaveProcessor } from '@features/map/model/processors/mapSettingsSaveProcessor.js';
import { mapTypeGaProcessor } from '@features/map/model/processors/mapTypeGaProcessor.js';
import { overlayPlacementProcessor } from '@features/map/model/processors/overlayPlacementProcessor.js';
import { exportMapFeaturesProcessor } from '@features/mapFeaturesExport/model/processors/exportMapFeaturesProcessor.js';
import { catalogMapsLoadProcessor } from '@features/mapLibrary/model/processors/catalogMapsLoadProcessor.js';
import { mapLibraryLoadProcessor } from '@features/mapLibrary/model/processors/mapLibraryLoadProcessor.js';
import {
  mapLibraryPreviewEndProcessor,
  mapLibraryPreviewGoneProcessor,
  mapLibraryPreviewModalProcessor,
  mapLibraryPreviewStartProcessor,
} from '@features/mapLibrary/model/processors/previewProcessors.js';
import { featuresHiddenResetProcessor } from '@features/mapSettings/model/featuresHiddenResetProcessor.js';
import { mapLayersPanelToggleProcessor } from '@features/mapSettings/model/mapLayersPanelToggleProcessor.js';
import { measurementProcessor } from '@features/measurement/model/measurementProcessor.js';
import { mapsDeleteProcessor } from '@features/myMaps/model/processors/mapsDeleteProcessor.js';
import { mapsLoadListProcessor } from '@features/myMaps/model/processors/mapsLoadListProcessor.js';
import { mapsLoadProcessor } from '@features/myMaps/model/processors/mapsLoadProcessor.js';
import {
  mapsOfflinePurgeProcessor,
  mapsSetAllOfflineProcessor,
  mapsSetMapOfflineProcessor,
} from '@features/myMaps/model/processors/mapsOfflineProcessor.js';
import {
  mapsOutboxProcessor,
  mapsResolveOutboxProcessor,
} from '@features/myMaps/model/processors/mapsOutboxProcessor.js';
import { mapsRestoreProcessor } from '@features/myMaps/model/processors/mapsRestoreProcessor.js';
import { mapsSaveProcessor } from '@features/myMaps/model/processors/mapsSaveProcessor.js';
import { mapsWorkingCopyProcessor } from '@features/myMaps/model/processors/mapsWorkingCopyProcessor.js';
import {
  objectDetailsProcessor,
  objectsTerrainOverlayProcessor,
} from '@features/objects/model/objectDetailsProcessor.js';
import {
  objectsChangePredicateProcessor,
  objectsFetchProcessor,
  objectsReleaseProcessor,
} from '@features/objects/model/objectsFetchProcessor.js';
import { objectsLookupProcessor } from '@features/objects/model/objectsLookupProcessor.js';
import { objectsRouteProcessor } from '@features/objects/model/objectsRouteProcessor.js';
import { downloadMapProcessor } from '@features/offlineMapExport/model/downloadMapProcessor.js';
import { openInExternalAppProcessor } from '@features/openInExternalApp/openInExternalAppProcessor.js';
import { osmLoadProcessor } from '@features/osm/model/processors/osmLoadProcessor.js';
import { panoramaFixProcessor } from '@features/panorama/model/processors/panoramaFixProcessor.js';
import { panoramaLookAtProcessor } from '@features/panorama/model/processors/panoramaLookAtProcessor.js';
import {
  panoramaReleaseProcessor,
  panoramaRenderProcessor,
} from '@features/panorama/model/processors/panoramaRenderProcessor.js';
import { panoramaToposcopeProcessor } from '@features/panorama/model/processors/panoramaToposcopeProcessor.js';
import { purchaseProcessor } from '@features/purchases/model/processors/purchaseProcessor.js';
import { routePlannerColorizeProcessor } from '@features/routePlanner/model/processors/colorizeProcessor.js';
import { routePlannerFindRouteProcessor } from '@features/routePlanner/model/processors/findRouteProcessor.js';
import { routePlannerMidpointHintProcessor } from '@features/routePlanner/model/processors/midpointHintProcessor.js';
import { routePlannerOptimizeOrderProcessor } from '@features/routePlanner/model/processors/optimizeOrderProcessor.js';
import { routePlannerRefocusMapProcessor } from '@features/routePlanner/model/processors/refocusMapProcessor.js';
import { routePlannerSetFromCurrentPositionProcessor } from '@features/routePlanner/model/processors/setFromCurrentPositionProcessor.js';
import * as rpcProcessors from '@features/rpc/model/processors.js';
import {
  searchHighlightProcessor,
  searchHighlightTrafo,
} from '@features/search/model/processors/searchHighlightProcessor.js';
import { searchPreviewProcessor } from '@features/search/model/processors/searchPreviewProcessor.js';
import { searchProcessor } from '@features/search/model/processors/searchProcessor.js';
import { toastsAddProcessor } from '@features/toasts/model/processors/toastsAddProcessor.js';
import { toastsCancelTypeProcessor } from '@features/toasts/model/processors/toastsCancelTypeProcessor.js';
import { toastsRemoveProcessor } from '@features/toasts/model/processors/toastsRemoveProcessor.js';
import { toastsRestartTimeoutProcessor } from '@features/toasts/model/processors/toastsRestartTimeoutProcessor.js';
import { toastsStopTimeoutProcessor } from '@features/toasts/model/processors/toastsStopTimeoutProcessor.js';
import { toposcopeFixProcessor } from '@features/toposcope/model/toposcopeFixProcessor.js';
import * as trackingAccessTokenProcessors from '@features/tracking/model/processors/trackingAccessTokenProcessors.js';
import { trackingCopyToDataViewerProcessor } from '@features/tracking/model/processors/trackingCopyProcessor.js';
import * as trackingDeviceProcessors from '@features/tracking/model/processors/trackingDeviceProcessors.js';
import { trackingFollowProcessor } from '@features/tracking/model/processors/trackingFollowProcessors.js';
import {
  viewshedCancelProcessor,
  viewshedFixProcessor,
  viewshedLayerProcessor,
  viewshedReleaseProcessor,
  viewshedRenderProcessor,
} from '@features/viewshed/model/processors.js';
import {
  weatherRadarLayerProcessor,
  weatherRadarRefreshProcessor,
} from '@features/weatherRadar/model/processors.js';
import { wikiLayerProcessor } from '@features/wiki/model/processors/wikiLayerProcessor.js';
import { wikiLoadPreviewProcessor } from '@features/wiki/model/processors/wikiLoadPreviewProcessor.js';
import { cancelProcessor } from '@/processors/cancelProcessor.js';
import { convertToDataViewerProcessor } from '@/processors/convertToDataViewerProcessor.js';
import { convertToDrawingProcessor } from '@/processors/convertToDrawingProcessor.js';
import { deleteProcessor } from '@/processors/deleteProcessor.js';
import { errorProcessor } from '@/processors/errorProcessor.js';
import { legacyMapWarningProcessor } from '@/processors/legacyMapWarningProcessor.js';
import { openToolProcessor } from '@/processors/openToolProcessor.js';
import { resetAppProcessor } from '@/processors/resetAppProcessor.js';
import { saveSettingsProcessor } from '@/processors/saveSettingsProcessor.js';
import { setActiveModalTransformer } from '@/processors/setActiveModalProcessor.js';
import { urlProcessor } from '../url/urlProcessor.js';

export const processors = [
  errorProcessor,
  toastsCancelTypeProcessor,
  cancelProcessor,
  mapLibraryLoadProcessor,
  catalogMapsLoadProcessor,
  mapSettingsSaveProcessor,
  mapLayersPanelToggleProcessor,
  featuresHiddenResetProcessor,
  mapLibraryPreviewStartProcessor,
  mapLibraryPreviewEndProcessor,
  mapLibraryPreviewModalProcessor,
  mapLibraryPreviewGoneProcessor,
  openToolProcessor,
  deleteProcessor,
  geoipProcessor,
  convertToDrawingProcessor,
  convertToDataViewerProcessor,
  cookieConsentProcessor,
  authLogoutProcessor,
  authDisconnectProcessor,
  authDeleteAccountProcessor,
  mapRefocusProcessor,
  mapFitBboxProcessor,
  getCountriesProcessor,
  searchProcessor,
  searchHighlightTrafo,
  searchHighlightProcessor,
  searchPreviewProcessor,
  locateProcessor,
  locateOnceProcessor,
  fixReadyProcessor,
  followLocationProcessor,
  saveSettingsProcessor,
  resetAppProcessor,
  drawingDiscardPartialLineProcessor,
  measurementProcessor,
  changesetsProcessor,
  changesetsTrackProcessor,
  changesetsInspectProcessor,
  changesetInspectorProcessor,
  changesetsHistoryProcessor,
  changesetsHistorySelectionProcessor,
  changesetsReturnToHistoryProcessor,
  authInitProcessor,
  authTrackProcessor,
  l10nSetLanguageProcessor,
  elevationChartProcessor,
  elevationChartPendingTargetProcessor,
  objectsFetchProcessor,
  objectsReleaseProcessor,
  objectsChangePredicateProcessor,
  objectDetailsProcessor,
  objectsTerrainOverlayProcessor,
  objectsRouteProcessor,
  objectsLookupProcessor,
  osmLoadProcessor,
  mapTypeGaProcessor,
  overlayPlacementProcessor,
  toastsAddProcessor,
  toastsRemoveProcessor,
  toastsRestartTimeoutProcessor,
  toastsStopTimeoutProcessor,
  dataViewerSetTrackDataProcessor,
  dataViewerStoreProcessor,
  dataViewerForgetStoredProcessor,
  dataViewerRestoreStoredProcessor,
  dataViewerDownloadTrackProcessor,
  dataViewerGpxLoadProcessor,
  dataViewerMatchTrackProcessor,
  dataViewerUploadToOsmProcessor,
  dataViewerCheckOsmAuthorizationProcessor,
  dataViewerToggleElevationChartProcessor,
  dataViewerResolveElevationPromptProcessor,
  dataViewerSelectProcessor,
  dataViewerDeleteFeatureProcessor,
  dataViewerJoinTracksProcessor,
  dataViewerDensifyProcessor,
  routePlannerFindRouteProcessor,
  routePlannerOptimizeOrderProcessor,
  galleryDeletePictureProcessor,
  galleryFetchUsersProcessor,
  galleryRequestImageProcessor,
  galleryRequestImagesByOrderProcessor,
  galleryRequestImagesByRadiusProcessor,
  gallerySavePictureProcessor,
  galleryShowImageGaProcessor,
  galleryShowOnTheMapProcessor,
  gallerySetItemForPositionPickingProcessor,
  gallerySubmitCommentProcessor,
  gallerySubmitStarsProcessor,
  galleryUploadModalProcessor,
  galleryQuickAddTagProcessor,
  galleryItemUploadProcessor,
  galleryMakeAllPremiumOrFreeProcessor,
  galleryAllOfLicenseProcessor,
  routePlannerRefocusMapProcessor,
  routePlannerColorizeProcessor,
  routePlannerSetFromCurrentPositionProcessor,
  routePlannerMidpointHintProcessor,
  ...Object.values(trackingAccessTokenProcessors),
  ...Object.values(trackingDeviceProcessors),
  trackingFollowProcessor,
  trackingCopyToDataViewerProcessor,
  gpsRecorderStartProcessor,
  gpsRecorderPauseProcessor,
  gpsRecorderStopProcessor,
  gpsRecorderSyncProcessor,
  gpsRecorderPushedStatusProcessor,
  gpsRecorderClearProcessor,
  gpsRecorderToolProcessor,
  setActiveModalTransformer,
  mapsLoadListProcessor,
  mapsLoadProcessor,
  mapsDeleteProcessor,
  mapsSaveProcessor,
  mapsWorkingCopyProcessor,
  mapsRestoreProcessor,
  mapsSetMapOfflineProcessor,
  mapsSetAllOfflineProcessor,
  mapsOfflinePurgeProcessor,
  mapsOutboxProcessor,
  mapsResolveOutboxProcessor,
  wikiLayerProcessor,
  wikiLoadPreviewProcessor,
  weatherRadarLayerProcessor,
  weatherRadarRefreshProcessor,
  legendProcessor,
  legacyMapWarningProcessor,
  openInExternalAppProcessor,
  panoramaRenderProcessor,
  panoramaReleaseProcessor,
  panoramaFixProcessor,
  panoramaLookAtProcessor,
  panoramaToposcopeProcessor,
  viewshedRenderProcessor,
  viewshedReleaseProcessor,
  viewshedCancelProcessor,
  viewshedLayerProcessor,
  viewshedFixProcessor,
  toposcopeFixProcessor,
  ...Object.values(rpcProcessors),
  exportMapFeaturesProcessor,
  ...authProcessors,
  downloadMapProcessor,
  purchaseProcessor,
  cacheTilesStartProcessor,
  cacheTilesRestartProcessor,
  cacheTilesStopProcessor,
  browseCacheSettingsProcessor,
  browseCacheLayersProcessor,
  browseCacheOpenProcessor,
  browseCacheClearProcessor,
  cachedMapDeletedProcessor,
  cachedMapEditedProcessor,
  urlProcessor,
];
